import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { eur } from "@/lib/format";
import { useT } from "@/i18n";
import { PageHeader, StatCard } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ChevronLeft, Wallet, Clock, CheckCircle2, Circle, CalendarClock, ExternalLink, Banknote, TrendingUp,
} from "lucide-react";

const PERIODS = [
  { id: "month", labelKey: "earn.p_month" },
  { id: "quarter", labelKey: "earn.p_quarter" },
  { id: "year", labelKey: "earn.p_year" },
  { id: "all", labelKey: "earn.p_all" },
];
const SOURCE_COLORS = {
  inner_circle: "bg-amber-500",
  local_circle: "bg-blue-500",
  sessions: "bg-primary",
  affiliate: "bg-violet-500",
  paid_content: "bg-teal-500",
  other: "bg-slate-400",
};
const SOURCE_LABEL_KEYS = {
  inner_circle: "earn.src_inner",
  local_circle: "earn.src_local",
  sessions: "earn.src_sessions",
  affiliate: "earn.src_affiliate",
  paid_content: "earn.src_paid",
  other: "earn.src_other",
};
const PAYOUT_STATUS = {
  paid: { labelKey: "earn.paid_status", cls: "bg-emerald-100 text-emerald-700" },
  pending: { labelKey: "earn.pending_status", cls: "bg-amber-100 text-amber-700" },
  failed: { labelKey: "earn.failed_status", cls: "bg-rose-100 text-rose-700" },
};
const STEP_LABEL_KEYS = {
  connect_account: "earn.step_connect",
  identity_verify: "earn.step_identity",
  bank_account: "earn.step_bank",
  documents: "earn.step_documents",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE", { dateStyle: "medium" }); }
  catch { return String(iso).slice(0, 10); }
}

export default function Earnings() {
  const t = useT();
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [overview, setOverview] = useState(null);
  const [bySource, setBySource] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [connect, setConnect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [ov, src] = await Promise.all([
      api.get(`/coach/earnings/overview?period=${period}`).then((r) => r.data).catch(() => null),
      api.get(`/coach/earnings/by-source?period=${period}`).then((r) => r.data).catch(() => null),
    ]);
    setOverview(ov);
    setBySource(src);
    setLoading(false);
  }, [period]);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    load();
  }, [user, load]);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    api.get("/coach/earnings/payouts").then(({ data }) => setPayouts(data?.payouts || [])).catch(() => setPayouts([]));
    api.get("/coach/earnings/stripe-connect-status").then(({ data }) => setConnect(data)).catch(() => setConnect(null));
  }, [user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const startStripe = async () => {
    setOnboarding(true);
    try {
      const { data } = await api.post("/coach/connect/onboard");
      const url = data?.url || data?.onboarding_url || data?.account_link_url;
      if (url) window.open(url, "_blank", "noopener");
      else toast.error(t("earn.stripe_err"));
    } catch (err) { toast.error(apiError(err, t("earn.stripe_err"))); }
    finally { setOnboarding(false); }
  };

  const o = overview || {};
  const sources = (bySource?.sources || []).filter((s) => s.cents > 0);

  return (
    <div data-testid="coach-earnings-page">
      <Seo title={t("earn.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="earnings-back">
        <ChevronLeft className="h-4 w-4" /> {t("earn.back")}
      </Link>
      <PageHeader eyebrow={t("earn.eyebrow")} title={t("earn.title")} subtitle={t("earn.subtitle")} />

      {/* Period */}
      <div className="mb-5 flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button key={p.id} type="button" onClick={() => setPeriod(p.id)} data-testid={`earn-period-${p.id}`}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${period === p.id ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {t(p.labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label={t("earn.gross")} value={eur(o.gross_cents ?? 0)} icon={TrendingUp} testId="earn-gross" />
          <StatCard label={t("earn.paid_out")} value={eur(o.paid_out_cents ?? 0)} icon={CheckCircle2} testId="earn-paid" />
          <StatCard label={t("earn.pending")} value={eur(o.pending_cents ?? 0)} icon={Clock} testId="earn-pending" />
          <StatCard label={t("earn.next_payout")} value={o.next_payout_date ? fmtDate(o.next_payout_date) : "—"} icon={CalendarClock} testId="earn-next" />
        </div>
      )}

      {/* By source */}
      <section className="mt-8 rounded-2xl border border-border bg-card p-5" data-testid="earn-by-source">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">{t("earn.by_source")}</p>
          <p className="font-display text-lg font-semibold text-foreground">{eur(bySource?.total_cents ?? 0)}</p>
        </div>
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("earn.no_revenue")}</p>
        ) : (
          <div className="space-y-3">
            {sources.map((s) => (
              <div key={s.key} data-testid={`earn-source-${s.key}`}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-foreground">{SOURCE_LABEL_KEYS[s.key] ? t(SOURCE_LABEL_KEYS[s.key]) : s.label}</span>
                  <span className="text-muted-foreground">{eur(s.cents)} · {Math.round((s.share || 0) * 100)}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-secondary">
                  <div className={`h-full rounded-full ${SOURCE_COLORS[s.key] || "bg-primary"}`} style={{ width: `${Math.max(2, Math.round((s.share || 0) * 100))}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Stripe status */}
      {connect && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="earn-stripe">
          <div className="mb-3 flex items-center gap-2">
            <Banknote className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold text-foreground">{t("earn.stripe_payouts")}</p>
            {connect.fully_active
              ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">{t("earn.active")}</span>
              : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">{t("earn.setup_needed")}</span>}
          </div>
          <div className="space-y-2">
            {(connect.steps || []).map((step) => (
              <div key={step.key} className="flex items-center gap-3" data-testid={`earn-step-${step.key}`}>
                {step.done ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                <span className="flex-1 text-sm text-foreground">{STEP_LABEL_KEYS[step.key] ? t(STEP_LABEL_KEYS[step.key]) : step.label}</span>
              </div>
            ))}
          </div>
          {!connect.fully_active && (
            <Button variant="outline" size="sm" className="mt-4 gap-1.5 rounded-full" onClick={startStripe} disabled={onboarding} data-testid="earn-stripe-onboard">
              {onboarding ? t("earn.opening_stripe") : (connect.connected ? t("earn.continue_onboarding") : t("earn.connect_stripe"))}
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}
        </section>
      )}

      {/* Payouts */}
      <section className="mt-6" data-testid="earn-payouts">
        <div className="mb-3 flex items-center gap-2">
          <Wallet className="h-4 w-4 text-primary" />
          <p className="text-sm font-semibold text-foreground">{t("earn.payouts")}</p>
        </div>
        {payouts === null ? (
          <Skeleton className="h-16 rounded-2xl" />
        ) : payouts.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">{t("earn.no_payouts")}</p>
        ) : (
          <div className="space-y-2">
            {payouts.map((p, i) => {
              const st = PAYOUT_STATUS[p.status] || { label: p.status, cls: "bg-secondary text-muted-foreground" };
              return (
                <div key={p.reference || i} data-testid={`payout-row-${i}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground">{eur(p.amount_cents || 0)}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.status === "paid" && p.paid_at ? t("earn.paid_on", { date: fmtDate(p.paid_at) }) : t("earn.created_on", { date: fmtDate(p.created_at) })}
                      {p.method ? ` · ${p.method}` : ""}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${st.cls}`}>{st.labelKey ? t(st.labelKey) : st.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
